import React, {useEffect, useMemo, useRef, useState} from 'react';
import AppModal from "../../core/components/app-modal";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import useModalWithData from "@common/hooks/use-modal-with-data";
import AppButton from "@common/components/app-button";
import {classNames} from "@utils/class-names";
import ClipboardHelper from "@utils/clipboard-helper";
import NotificationService from "@common/services/notification.service";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {stripHtmlToText} from "../helpers/strip-html-to-text";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../../core/animations";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";
import sanitizeUrlString from "@utils/sanitize-url-string";

export interface ShareArticleModalData {
    articleId?: string;
    articleTitle?: string;
    shareUrl?: string;
}

const formatPhoneForDisplay = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)].filter(Boolean);
    return parts.join('-');
}

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const ShareArticleModal: React.FC = () => {
    const {data, hideModal, isOpenModal} = useModalWithData<ShareArticleModalData>(ModalIds.SHARE_ARTICLE);
    const [phone, setPhone] = useState<string>('');
    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'medium');
    const containerRef = useRef<HTMLDivElement>(null);
    const {shareArticle} = useArticleApi();
    const {isLoading} = useLoading();

    const articleTitle = data?.articleTitle ?? '';
    const fallbackUrl = useMemo(() => {
        const id = data?.articleId ?? '';
        if (!id) return window.location.origin;
        const plainTextTitle = convertLexicalJsonToPlainText(articleTitle);
        return `${window.location.origin}/article/${sanitizeUrlString(plainTextTitle)}`;
    }, [data?.articleId]);
    const shareUrl = data?.shareUrl || fallbackUrl;

    const isPhoneValid = useMemo(() => onlyDigits(phone).length === 9, [phone]);

    const onCopyLink = async () => {
        await ClipboardHelper.copyText(shareUrl,
            () => NotificationService.success('Link skopiowany do schowka.'),
            () => NotificationService.error('Nie udało się skopiować linku')
        );
    };

    const handleCloseModal = () => closeModal(() => hideModal())

    useEffect(() => {
        if (!isOpenModal()) return;
        const onMouseDown = (e: MouseEvent) => {
            const node = containerRef.current;
            if (node && !node.contains(e.target as Node)) {
                handleCloseModal();
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModal()]);

    const onSendSms = async () => {
        if (!isPhoneValid || !data?.articleId) return;
        const sanitized = onlyDigits(phone);
        try {
            await shareArticle(data.articleId, sanitized);
            NotificationService.success('Wysłano SMS z linkiem do artykułu.');
            handleCloseModal();
        } catch (e: any) {
            NotificationService.error(e?.message || 'Nie udało się wysłać SMS.');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(formatPhoneForDisplay(e.target.value));
    };

    if (!data) return null;

    return (
        <AppModal
            id={ModalIds.SHARE_ARTICLE}
            key={ModalIds.SHARE_ARTICLE}
            forceFixed={true}
            zIndex={40}
            classModal="!w-fit !min-w-fit">
            <animated.div
                style={springProps}
                className="w-full max-w-[655px] bg-surface-level-1 rounded-2xl border border-outline-mild shadow-lvl-2"
                ref={containerRef}
            >
                <h3 className="flex items-center text-lg font-semibold text-on-surface leading-[22px] py-3 pr-4 pl-8 min-h-[72px] h-[92px]">
                    Udostępnij “{stripHtmlToText(convertLexicalJsonToHtml(articleTitle))}”
                </h3>

                <div className="py-6 px-8 space-y-6">
                    <p className="text-base font-normal text-on-surface/80 leading-5">
                        Podaj numer telefonu, na który chcesz wysłać link do artykułu, lub skopiuj bezpośredni link
                    </p>

                    <input
                        type="tel"
                        value={phone}
                        onChange={handleChange}
                        placeholder="xxx-xxx-xxx"
                        maxLength={11}
                        className={classNames(
                            "w-full p-3 border rounded-lg text-sm h-[40px]",
                            isPhoneValid ? "border-outline-mild" : "border-outline"
                        )}
                    />
                </div>

                <div className="flex flex-wrap md:flex-nowrap gap-2 justify-end items-center mt-6 py-3 md:py-0 md:h-[72px] md:min-h-[72px] px-8">
                    <AppButton
                        onClick={onCopyLink}
                        className="w-full md:w-fit h-[40px] font-semibold border-2 border-on-surface hover:bg-on-surface/5 rounded-full"
                    >
                        Kopiuj link
                    </AppButton>
                    <AppButton
                        onClick={onSendSms}
                        disabled={!isPhoneValid || !data?.articleId || isLoading(["shareArticle"])}
                        className="w-full md:w-fit h-[40px] bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-60 rounded-full"
                    >
                        Wyślij sms
                    </AppButton>
                </div>
            </animated.div>
        </AppModal>
    );
};

export default ShareArticleModal;


